import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";

import {
  cascadeHideUser,
  listNewWriterArticles,
  listNewWriters,
  setUserVerified,
  type ListNewWritersResponse,
  type ListNewWriterArticlesResponse,
  type NewWriterWindow,
} from "@/lib/admin-api";

const NEW_WRITERS_KEY = "new-writers" as const;

export function useNewWriters(window: NewWriterWindow, page: number, pageSize: number) {
  const { data, isPending, isError, error } = useQuery<ListNewWritersResponse>({
    queryKey: [NEW_WRITERS_KEY, window, page, pageSize],
    queryFn: () => listNewWriters({ window, page, pageSize }),
  });
  return {
    writers: data?.writers ?? [],
    pageInfo: data?.pageInfo,
    loading: isPending,
    failed: isError,
    error,
  };
}

export function useNewWriterArticles(creatorId: string, window: NewWriterWindow, enabled: boolean) {
  const { data, isPending, isError, error } = useQuery<ListNewWriterArticlesResponse>({
    queryKey: [NEW_WRITERS_KEY, "articles", creatorId, window],
    queryFn: () => listNewWriterArticles(creatorId, window),
    enabled,
  });
  return {
    articles: data?.articles ?? [],
    loading: isPending,
    failed: isError,
    error,
  };
}

/** Marks a writer verified. See setUserVerified's own doc comment. */
export function useVerifyWriter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, verified }: { userId: string; verified: boolean }) =>
      setUserVerified(userId, verified),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NEW_WRITERS_KEY] });
    },
  });
}

/** Hides every article a writer has published. See cascadeHideUser's own doc comment. */
export function useHideAllWriterContent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ creatorId, note }: { creatorId: string; note: string }) =>
      cascadeHideUser(creatorId, "MODERATION_ACTION_HIDE", note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [NEW_WRITERS_KEY] });
    },
  });
}
